import * as React from 'react';
import { computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { cs } from '@shared/utils/utils-classname';
import { Drawer } from '@shared/ui-native/Drawer';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Notify } from '@shared/ui-native/Notify';
import { MaterialsStore } from '@pages/trucks/store/MaterialsStore';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from '@shared/ui-react/Button';
import { DialogConfirm } from '@shared/ui-native/Dialog';
import { DateUtils } from '@shared/utils/DateUtils';
import { Message } from 'primereact/message';
const MAX_NAME_LENGTH = 45;

type FactorKey = 'm3LooseToM3Solid' | 'm3LooseToTons' | 'm3LooseToPieces';

const FACTOR_KEYS: FactorKey[] = ['m3LooseToM3Solid', 'm3LooseToTons', 'm3LooseToPieces'];

function factorToInputString(value: number | null | undefined): string {
    return value == null ? '' : String(value);
}

function parseFactor(raw: string): { value: number | null; invalid: boolean } {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return { value: null, invalid: false };
    const normalized = trimmed.replace(',', '.');
    const num = Number(normalized);
    if (!isFinite(num) || num <= 0) return { value: null, invalid: true };
    return { value: num, invalid: false };
}

export const MaterialEditor = {
    open(materialsStore: MaterialsStore, editItem?: IMaterial, onClose?: () => void, requireDensity?: boolean, hideLockWarning?: boolean) {
        Drawer.close();
        const el = Drawer.open(380);
        ReactUtils.safeRender(<MaterialCreateUpdate materialsStore={materialsStore} editItem={editItem} onClose={onClose} requireDensity={requireDensity ?? false} hideLockWarning={hideLockWarning ?? false} />, el);
    },
    close() {
        Drawer.close();
    },
};

@observer
class MaterialCreateUpdate extends React.Component<{
    materialsStore: MaterialsStore;
    editItem?: IMaterial;
    onClose?: () => void;
    requireDensity: boolean;
    hideLockWarning: boolean;
}> {
    @observable name: string = '';
    @observable isLoading: boolean = false;
    @observable m3LooseToM3Solid: string = '';
    @observable m3LooseToTons: string = '';
    @observable m3LooseToPieces: string = '';

    constructor(props: any) {
        super(props);
        makeObservable(this);
        if (props.editItem) {
            this.name = props.editItem.material;
            this.m3LooseToM3Solid = factorToInputString(props.editItem.m3LooseToM3Solid);
            this.m3LooseToTons = factorToInputString(props.editItem.m3LooseToTons);
            this.m3LooseToPieces = factorToInputString(props.editItem.m3LooseToPieces);
        }
    }

    get isEdit() {
        return !!this.editItem?.id;
    }

    get trimmedName() {
        return this.name.trim();
    }

    get editItem() {
        const editItemId = this.props.editItem?.id;
        if (!editItemId) {
            return this.props.editItem;
        }

        return this.props.materialsStore.items.find(item => item.id === editItemId) ?? this.props.editItem;
    }

    get isFactorsLocked() {
        return !!this.editItem?.factorsLockedAt;
    }

    get canLockFactors() {
        const isAdmin = kuura.hasPrivilege('ORGANIZATION_ADMIN') || kuura.hasPrivilege('SUPER_USER');
        const hasAnyFactor = this.editItem.m3LooseToM3Solid || this.editItem.m3LooseToPieces || this.editItem.m3LooseToTons;
        return !!this.editItem?.uuid && isAdmin && !this.isFactorsLocked && hasAnyFactor;
    }

    get canUnlockFactors() {
        return !!this.editItem?.uuid && this.isFactorsLocked && this.editItem?.factorsLockedBy?.id === kuura.getUserId();
    }

    @computed get parsedFactors(): Record<FactorKey, { value: number | null; invalid: boolean }> {
        return {
            m3LooseToM3Solid: parseFactor(this.m3LooseToM3Solid),
            m3LooseToTons: parseFactor(this.m3LooseToTons),
            m3LooseToPieces: parseFactor(this.m3LooseToPieces),
        };
    }

    get hasChanges() {
        if (!this.isEdit) {
            return false;
        }

        const editItem = this.editItem;

        const { m3LooseToM3Solid, m3LooseToTons, m3LooseToPieces } = this.parsedFactors;

        return (
            this.trimmedName !== editItem.material ||
            m3LooseToM3Solid.value !== (editItem.m3LooseToM3Solid ?? null) ||
            m3LooseToTons.value !== (editItem.m3LooseToTons ?? null) ||
            m3LooseToPieces.value !== (editItem.m3LooseToPieces ?? null)
        );
    }

    get isDuplicate() {
        const editId = this.editItem?.id;
        return this.props.materialsStore.items.some(m => m.material === this.trimmedName && m.id !== editId);
    }

    @computed get factorErrors(): Record<FactorKey, boolean> {
        return {
            m3LooseToM3Solid: this.parsedFactors.m3LooseToM3Solid.invalid,
            m3LooseToTons: this.parsedFactors.m3LooseToTons.invalid,
            m3LooseToPieces: this.parsedFactors.m3LooseToPieces.invalid,
        };
    }

    @computed get hasAnyFactorError() {
        return FACTOR_KEYS.some(k => this.factorErrors[k]);
    }

    get isFormValid() {
        const missingRequiredDensity = this.props.requireDensity && !this.isEdit && this.parsedFactors.m3LooseToTons.value == null;
        return this.trimmedName.length > 0 && !this.isDuplicate && !this.hasAnyFactorError && !missingRequiredDensity;
    }

    render() {
        const isEdit = this.isEdit;
        const isDuplicate = this.isDuplicate;
        const isBusy = this.isLoading || this.props.materialsStore.isDoingAsyncWork;
        const editItem = this.editItem;

        return (
            <div className={cs({ 'overlay-loading': isBusy })}>
                <header className='padded'>
                    <h4>{__(isEdit ? 'general.edit' : 'general.create')}</h4>
                </header>

                <div className='form-group'>
                    <label>{__('general.name')} *</label>
                    <input
                        type='text'
                        autoFocus
                        disabled={isBusy}
                        maxLength={MAX_NAME_LENGTH}
                        value={this.name}
                        onChange={e => {
                            if (e.currentTarget.value.length >= MAX_NAME_LENGTH) {
                                Notify.warn(__('general.validation.maximum_n_field_length', String(MAX_NAME_LENGTH)));
                            }
                            this.name = e.currentTarget.value;
                        }}
                    />
                    {isDuplicate && <div style={{ color: '#cc0000', marginTop: 5 }}>{__('general.validation.name_already_exists')}</div>}
                </div>

                <div className='form-group'>
                    <label style={{ paddingBottom: 10 }}>{__('trucks.material.factors.heading')}</label>

                    {this.renderFactorInput('m3LooseToM3Solid', __('trucks.m3_solid'), __('trucks.m3_solid'), false)}
                    {this.renderFactorInput('m3LooseToTons', __('trucks.tons'), __('trucks.ton_singular'), this.props.requireDensity && !this.isEdit)}
                    {this.renderFactorInput('m3LooseToPieces', __('trucks.pieces'), __('trucks.piece_singular'), false)}

                    {this.props.requireDensity && !this.isEdit && this.parsedFactors.m3LooseToTons.value == null && !this.parsedFactors.m3LooseToTons.invalid && (
                        <div style={{ color: '#cc0000', marginTop: 3 }}>{__('general.validation.required')}</div>
                    )}

                    {this.isFactorsLocked ? (
                        <Message
                            severity='info'
                            text={__('js_saved_views.locked_by_username_on_date', editItem?.factorsLockedBy?.username ?? '', DateUtils.formatDateTime(editItem?.factorsLockedAt))}
                            style={{ width: '100%', marginBottom: 15 }}
                        />
                    ) : (
                        !this.props.hideLockWarning && (
                            <Message severity='warn' text={__('trucks.materials.unlocked_warning_2')} style={{ width: '100%', marginBottom: 15 }} />
                        )
                    )}
                </div>

                <footer>
                    <div className='flex-spread'>
                        <div>
                            {isEdit && this.canLockFactors && (
                                <Button secondary subtle icon='iki iki-locked' onClick={this.confirmLock} disabled={isBusy}>
                                    {__('general.lock')}
                                </Button>
                            )}
                            {isEdit && this.canUnlockFactors && (
                                <Button secondary subtle icon='iki iki-locked' onClick={this.confirmUnlock} disabled={isBusy}>
                                    {__('general.unlock')}
                                </Button>
                            )}
                        </div>

                        <div className='flex-row gap-half'>
                            <Button secondary subtle onClick={this.handleClose} disabled={isBusy}>
                                {__('general.close')}
                            </Button>
                            <Button primary onClick={this.submit} disabled={!this.isFormValid || isBusy} loading={isBusy}>
                                {__(isEdit ? 'general.save' : 'general.create')}
                            </Button>
                        </div>
                    </div>
                </footer>
            </div>
        );
    }

    renderFactorInput = (key: FactorKey, suffix: string, singularSuffix: string, required: boolean = false) => {
        const hasError = this.factorErrors[key];
        const parsed = this.parsedFactors[key];
        const inverse = parsed.value != null && parsed.value > 0 ? (1 / parsed.value).toFixed(2) : '\u2014';
        return (
            <div style={{ marginBottom: 8 }}>
                <table style={{ width: this.isFactorsLocked ? 'auto' : '100%' }}>
                    <tbody>
                        <tr>
                            <td className='no-break-text'>
                                {__('trucks.material.factors.m3_loose_to_other_unit')}
                                {required && <span style={{ color: '#cc0000', marginLeft: 2 }}>*</span>}
                            </td>
                            <td>
                                {this.isFactorsLocked ? (
                                    <strong style={{ paddingLeft: 10 }}>{this[key] ? parseFloat(this[key]) : '--'}</strong>
                                ) : (
                                    <InputNumber
                                        inputId={key}
                                        readOnly={this.isLoading || this.props.materialsStore.isDoingAsyncWork}
                                        min={0}
                                        step={0.01}
                                        minFractionDigits={0}
                                        maxFractionDigits={2}
                                        useGrouping={false}
                                        value={this[key] ? parseFloat(this[key]) : null}
                                        onValueChange={e => (this[key] = e.value != null ? String(e.value) : '')}
                                    />
                                )}
                            </td>
                            <td>{suffix}</td>
                        </tr>
                        <tr style={{ color: '#777', fontSize: 12 }}>
                            <td className='no-break-text'>1 {singularSuffix} =</td>
                            <td style={{ paddingLeft: 10 }}>{inverse}</td>
                            <td>{__('trucks.m3_loose')}</td>
                        </tr>
                    </tbody>
                </table>
                {hasError && <div style={{ color: '#cc0000', marginTop: 3 }}>{__('trucks.material.factors.error.positive')}</div>}
            </div>
        );
    };

    handleClose = () => {
        if (this.props.onClose) this.props.onClose();
        MaterialEditor.close();
    };

    submit = () => {
        this.save(true, false);
    };

    confirmLock = () => {
        DialogConfirm({ msg: __('trucks.materials.confirm_lock') }, () => {
            const editItem = this.editItem;
            if (this.hasChanges) {
                return this.save(false, true);
            }

            this.isLoading = true;
            return this.runAsyncRequest(editItem && this.props.materialsStore.lockFactors(editItem));
        });
    };

    confirmUnlock = () => {
        DialogConfirm({ msg: __('trucks.materials.confirm_unlock') }, () => {
            const editItem = this.editItem;
            this.isLoading = true;
            return this.runAsyncRequest(editItem && this.props.materialsStore.unlockFactors(editItem));
        });
    };

    save = (closeOnSuccess: boolean, lockAfterSave: boolean) => {
        if (!this.isFormValid) return false;

        const editItem = this.editItem;
        const name = this.trimmedName;
        const { m3LooseToM3Solid, m3LooseToTons, m3LooseToPieces } = this.parsedFactors;

        if (this.isEdit && !this.hasChanges) {
            this.handleClose();
            return false;
        }

        this.isLoading = true;

        const payload: IMaterial = {
            material: name,
            m3LooseToM3Solid: m3LooseToM3Solid.value,
            m3LooseToTons: m3LooseToTons.value,
            m3LooseToPieces: m3LooseToPieces.value,
        };
        const promise = this.isEdit && editItem ? this.props.materialsStore.update({ ...payload, id: editItem.id }, !lockAfterSave) : this.props.materialsStore.create(payload);

        if (!promise) {
            this.isLoading = false;
            return false;
        }

        let hasLockChain = false;
        promise
            .done(() => {
                if (lockAfterSave && this.isEdit && editItem?.id) {
                    hasLockChain = true;
                    const refreshedEditItem = this.props.materialsStore.items.find(item => item.id === editItem.id);
                    this.runAsyncRequest(refreshedEditItem && this.props.materialsStore.lockFactors(refreshedEditItem));
                    return;
                }

                if (closeOnSuccess) {
                    if (this.props.onClose) this.props.onClose();
                    MaterialEditor.close();
                }
            })
            .always(() => {
                if (!hasLockChain) {
                    this.isLoading = false;
                }
            });
        return promise;
    };

    runAsyncRequest = (promise: JQueryPromise<any> | false) => {
        if (!promise) {
            this.isLoading = false;
            return false;
        }

        promise.always(() => {
            this.isLoading = false;
        });
        return promise;
    };
}
